import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Loader2, Plus, Play, Pause, Check, X, Clock, ChevronRight,
  Copy, Bookmark, Trash2, ArrowRight, MoreHorizontal, Eye, Edit3
} from 'lucide-react';
import { toast } from 'sonner';

/* ─── Status configs ─── */
const STATUS_CFG = {
  running:       { color: '#2563EB', bg: 'rgba(37,99,235,0.1)',  label: 'In corso' },
  paused:        { color: '#7C3AED', bg: 'rgba(124,58,237,0.1)', label: 'Serve azione' },
  waiting_human: { color: '#7C3AED', bg: 'rgba(124,58,237,0.1)', label: 'Serve azione' },
  completed:     { color: '#16A34A', bg: 'rgba(22,163,74,0.1)',  label: 'Completato' },
  done:          { color: '#16A34A', bg: 'rgba(22,163,74,0.1)',  label: 'Completato' },
  draft:         { color: '#6B7280', bg: 'rgba(107,114,128,0.1)',label: 'Bozza' },
  active:        { color: '#2563EB', bg: 'rgba(37,99,235,0.1)',  label: 'Attivo' },
  failed:        { color: '#DC2626', bg: 'rgba(220,38,38,0.1)',  label: 'Errore' },
  error:         { color: '#DC2626', bg: 'rgba(220,38,38,0.1)',  label: 'Errore' },
};
const STEP_CFG = {
  done:          { color: '#16A34A', bg: 'rgba(22,163,74,0.08)',  border: '#E5E7EB' },
  completed:     { color: '#16A34A', bg: 'rgba(22,163,74,0.08)',  border: '#E5E7EB' },
  waiting_human: { color: '#7C3AED', bg: 'rgba(124,58,237,0.08)', border: '#7C3AED' },
  running:       { color: '#2563EB', bg: 'rgba(37,99,235,0.08)',  border: '#2563EB' },
  pending:       { color: '#9CA3AF', bg: 'rgba(156,163,175,0.05)',border: '#E5E7EB' },
  error:         { color: '#DC2626', bg: 'rgba(220,38,38,0.08)',  border: '#DC2626' },
  rejected:      { color: '#DC2626', bg: 'rgba(220,38,38,0.08)',  border: '#DC2626' },
};

const AGENT_SHORT = {
  copywriter_agent: 'CW', planner_agent: 'PL', strategist_agent: 'ST',
  scout_agent: 'SC', sales_agent: 'SA', funnel_agent: 'FN',
  events_agent: 'EV', designer_agent: 'DS', publisher_agent: 'PB',
  operator_agent: 'OP', analyst_agent: 'AN', partnership_agent: 'PA',
};

function agentIcon(step) {
  if (step.step_type === 'human') return '\u{1F464}';
  if (step.step_type === 'platform') return '\u{1F4E4}';
  return AGENT_SHORT[step.agent_name] || (step.agent_name || 'AI').slice(0, 2).toUpperCase();
}

function agentLabel(step) {
  if (step.step_type === 'human') return 'Umano';
  if (step.step_type === 'platform') return 'Piattaforma';
  return step.agent_name ? step.agent_name.replace('_agent', '').replace('_', ' ') : 'Agente';
}

function timeSince(d) {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60000))}m fa`;
  if (h < 24) return `${h}h fa`;
  const days = Math.floor(h / 24);
  return `${days}g fa`;
}


/* ─── Process List (Left Panel) ─── */
function ProcessList({ boards, selectedId, onSelect, onNewSituation, filter, setFilter }) {
  const filters = [
    { id: 'all', label: 'Tutti' },
    { id: 'active', label: 'Attivi' },
    { id: 'waiting', label: 'In attesa' },
    { id: 'completed', label: 'Completati' },
  ];

  const filtered = boards.filter(b => {
    if (filter === 'all') return true;
    if (filter === 'active') return ['running', 'paused', 'active', 'waiting_human'].includes(b.status);
    if (filter === 'waiting') return ['paused', 'waiting_human'].includes(b.status);
    if (filter === 'completed') return ['completed', 'done'].includes(b.status);
    return true;
  });

  const counts = {
    all: boards.length,
    active: boards.filter(b => ['running', 'paused', 'active', 'waiting_human'].includes(b.status)).length,
    waiting: boards.filter(b => ['paused', 'waiting_human'].includes(b.status)).length,
    completed: boards.filter(b => ['completed', 'done'].includes(b.status)).length,
  };

  return (
    <div className="flex h-full w-[320px] flex-col flex-shrink-0"
      style={{ borderRight: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div className="p-4" style={{ borderBottom: '1px solid #E5E7EB' }}>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-[16px] font-bold" style={{ color: '#1B2B4B' }}>Board operativa</h2>
          <button onClick={onNewSituation}
            className="rounded-lg px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#2563EB' }}
            data-testid="new-situation-btn">
            + Nuova situazione
          </button>
        </div>
        <p className="text-[12px]" style={{ color: '#7C8CA2' }}>
          {boards.length} processi &middot; {counts.waiting} in attesa
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto px-4 py-3" style={{ borderBottom: '1px solid #E5E7EB' }}>
        {filters.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className="whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-medium transition-colors"
            style={{
              backgroundColor: filter === f.id ? '#2563EB' : '#F3F4F6',
              color: filter === f.id ? '#FFFFFF' : '#6B7280',
            }}
            data-testid={`filter-${f.id}`}>
            {f.label} ({counts[f.id]})
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Nessun processo</p>
          </div>
        ) : filtered.map(board => {
          const cfg = STATUS_CFG[board.status] || STATUS_CFG.draft;
          const isSelected = board.id === selectedId;
          const totalSteps = board.steps?.length || 0;
          const completedSteps = board.completed_steps_count || (board.steps || []).filter(s => ['done', 'completed'].includes(s.status)).length;
          const pct = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
          const waitingSteps = (board.steps || []).filter(s => s.status === 'waiting_human').length
            + (board.status === 'paused' ? 1 : 0);
          const isUrgent = board.priority === 'high' || board.urgent;

          return (
            <button key={board.id} onClick={() => onSelect(board.id)}
              className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors"
              style={{
                borderLeft: `3px solid ${isSelected ? '#2563EB' : 'transparent'}`,
                backgroundColor: isSelected ? 'rgba(37,99,235,0.06)' : 'transparent',
                borderBottom: '1px solid #F3F4F6',
              }}
              data-testid={`board-item-${board.id}`}>
              {/* Progress circle */}
              <div className="relative flex h-[36px] w-[36px] flex-shrink-0 items-center justify-center rounded-full"
                style={{ background: `conic-gradient(${cfg.color} ${pct}%, #E5E7EB ${pct}%)` }}>
                <div className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-white text-[10px] font-bold"
                  style={{ color: cfg.color }}>
                  {pct}%
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-[13px] font-semibold truncate" style={{ color: '#1B2B4B' }}>
                    {board.title}
                  </span>
                  {isUrgent && (
                    <span className="rounded px-1.5 py-0.5 text-[9px] font-bold text-white flex-shrink-0"
                      style={{ backgroundColor: '#DC2626' }}>Urgente</span>
                  )}
                </div>
                <div className="mb-1 flex items-center gap-2">
                  <span className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                    style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                    {board.board_type === 'situazione' ? 'Situazione' : 'Processo'}
                  </span>
                  <span className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                    style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                    {cfg.label}
                  </span>
                </div>
                <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
                  {completedSteps}/{totalSteps} step
                  {waitingSteps > 0 && (
                    <> &middot; <Pause className="mb-0.5 inline h-3 w-3" /> {waitingSteps} in attesa</>
                  )}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


/* ─── Process Detail (Right Panel) ─── */
function ProcessDetail({ board, run, onAction, actionLoading, onDuplicate, onSaveTemplate, onDelete, onRun }) {
  const [showMenu, setShowMenu] = useState(false);
  const [actionNotes, setActionNotes] = useState({});
  const [expandedOutput, setExpandedOutput] = useState(null);

  if (!board) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center" style={{ backgroundColor: '#FAFBFC' }}>
        <Play size={48} style={{ color: '#D1D5DB' }} className="mb-4" />
        <p className="text-[15px] font-medium" style={{ color: '#4B5563' }}>Seleziona un processo</p>
        <p className="text-[13px] mt-1" style={{ color: '#9CA3AF' }}>oppure crea una nuova situazione</p>
      </div>
    );
  }

  const cfg = STATUS_CFG[board.status] || STATUS_CFG.draft;
  const totalSteps = board.steps?.length || 0;

  // Merge step statuses from run
  const steps = (board.steps || []).map(step => {
    if (run?.execution_plan) {
      const runStep = run.execution_plan.find(rs => rs.step_id === step.step_id);
      if (runStep) return { ...step, status: runStep.status, output: runStep.output, error: runStep.error, completed_at: runStep.completed_at };
    }
    return { ...step, status: step.status || 'pending' };
  });

  const completedSteps = steps.filter(s => ['done', 'completed'].includes(s.status)).length;
  const pct = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  const canRun = ['draft', 'active'].includes(board.status);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div className="p-4 flex items-center justify-between flex-shrink-0" style={{ borderBottom: '1px solid #E5E7EB' }}>
        <div>
          <div className="mb-1 flex items-center gap-2">
            <h1 className="text-[18px] font-bold" style={{ color: '#1B2B4B' }} data-testid="board-title">
              {board.title}
            </h1>
            <span className="rounded px-2 py-0.5 text-[11px] font-medium"
              style={{ backgroundColor: cfg.bg, color: cfg.color }}>
              {cfg.label}
            </span>
            {(board.priority === 'high' || board.urgent) && (
              <span className="rounded px-2 py-0.5 text-[11px] font-bold text-white"
                style={{ backgroundColor: '#DC2626' }}>Priorita alta</span>
            )}
          </div>
          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <div className="h-2 w-[200px] overflow-hidden rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: cfg.color }} />
            </div>
            <span className="text-[12px]" style={{ color: '#6B7280' }}>
              {completedSteps} di {totalSteps} step completati
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canRun && (
            <button onClick={onRun} disabled={actionLoading}
              className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-medium text-white hover:opacity-90"
              style={{ backgroundColor: '#2563EB' }}
              data-testid="run-board-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Avvia
            </button>
          )}
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)}
              className="rounded-lg p-2 transition-colors hover:bg-gray-50"
              style={{ border: '1px solid #E5E7EB' }}>
              <MoreHorizontal className="h-4 w-4" style={{ color: '#6B7280' }} />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full z-10 mt-1 w-[200px] rounded-lg bg-white py-1 shadow-lg"
                style={{ border: '1px solid #E5E7EB' }}>
                <button onClick={() => { onDuplicate(); setShowMenu(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-gray-50" style={{ color: '#1B2B4B' }}>
                  <Copy className="h-4 w-4" style={{ color: '#6B7280' }} />Duplica processo
                </button>
                <button onClick={() => { onSaveTemplate(); setShowMenu(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-gray-50" style={{ color: '#1B2B4B' }}>
                  <Bookmark className="h-4 w-4" style={{ color: '#6B7280' }} />Salva come template
                </button>
                <button onClick={() => { onDelete(); setShowMenu(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-red-50" style={{ color: '#DC2626' }}>
                  <Trash2 className="h-4 w-4" />Annulla processo
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto p-6" style={{ backgroundColor: '#FAFBFC' }}>
        <div className="relative pl-8">
          {/* Vertical line */}
          <div className="absolute bottom-0 left-[15px] top-0 w-[2px]" style={{ backgroundColor: '#E5E7EB' }} />

          {steps.map((step, idx) => {
            const sc = STEP_CFG[step.status] || STEP_CFG.pending;
            const isWaiting = step.status === 'waiting_human';
            const isDone = ['done', 'completed'].includes(step.status);
            const isRunning = step.status === 'running';
            const hasLongOutput = step.output && step.output.length > 300;
            const isExpanded = expandedOutput === step.step_id;

            return (
              <div key={step.step_id} className="relative mb-4 last:mb-0" data-testid={`step-${step.step_id}`}>
                {/* Dot */}
                <div className="absolute -left-8 top-4 flex h-[30px] w-[30px] items-center justify-center rounded-full border-2"
                  style={{
                    backgroundColor: isDone ? '#16A34A' : '#FFFFFF',
                    borderColor: sc.color,
                  }}>
                  {isDone ? <Check className="h-4 w-4 text-white" />
                    : isWaiting ? <Clock className="h-4 w-4" style={{ color: sc.color }} />
                    : isRunning ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: sc.color }} />
                    : <span className="text-[10px] font-bold" style={{ color: sc.color }}>{idx + 1}</span>}
                </div>

                {/* Step card */}
                <div className="rounded-lg p-4" style={{
                  backgroundColor: sc.bg,
                  border: `${isWaiting ? '2px' : '1px'} solid ${sc.border}`,
                }}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white"
                        style={{ backgroundColor: step.step_type === 'human' ? '#6B7280' : '#2563EB' }}>
                        {agentIcon(step)}
                      </div>
                      <span className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>
                        {agentLabel(step)}: {step.title}
                      </span>
                    </div>
                    {step.completed_at && (
                      <span className="text-[11px]" style={{ color: '#9CA3AF' }}>
                        completato {timeSince(step.completed_at)}
                      </span>
                    )}
                    {isWaiting && (
                      <span className="text-[11px]" style={{ color: '#9CA3AF' }}>in attesa</span>
                    )}
                  </div>

                  {/* Output */}
                  {step.output && (
                    <div className="mb-3">
                      {isWaiting && step.output.includes('\n') ? (
                        <p className="whitespace-pre-wrap text-[14px] leading-relaxed" style={{ color: '#374151' }}>
                          {hasLongOutput && !isExpanded ? step.output.slice(0, 300) + '...' : step.output}
                          {hasLongOutput && (
                            <button onClick={() => setExpandedOutput(isExpanded ? null : step.step_id)}
                              className="ml-1 text-[13px] font-medium" style={{ color: '#2563EB' }}>
                              {isExpanded ? 'Mostra meno' : 'Leggi tutto'}
                            </button>
                          )}
                        </p>
                      ) : (
                        <p className="text-[13px]" style={{ color: isDone ? '#16A34A' : '#374151' }}>
                          {isDone && '\u2713 '}{step.output}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Error */}
                  {step.error && (
                    <p className="text-[13px] mb-3" style={{ color: '#DC2626' }}>
                      \u26A0 {step.error}
                    </p>
                  )}

                  {/* Action area for waiting steps */}
                  {isWaiting && (
                    <div className="mt-3 rounded-lg p-3" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                      <div className="mb-2 flex items-center gap-1">
                        <span className="rounded px-2 py-0.5 text-[11px] font-medium"
                          style={{ backgroundColor: 'rgba(124,58,237,0.1)', color: '#7C3AED' }}>
                          Serve il tuo intervento
                        </span>
                      </div>
                      <div className="mb-3 flex gap-2">
                        <button disabled={actionLoading}
                          onClick={() => onAction(step.step_id, 'approve', step.output || '', actionNotes[step.step_id] || '')}
                          className="flex items-center gap-1 rounded-lg px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
                          style={{ backgroundColor: '#16A34A' }}
                          data-testid={`step-approve-${step.step_id}`}>
                          <Check className="h-4 w-4" /> Approva
                        </button>
                        <button disabled={actionLoading}
                          onClick={() => onAction(step.step_id, 'modify', step.output || '', actionNotes[step.step_id] || '')}
                          className="rounded-lg px-4 py-2 text-[13px] font-medium transition-colors hover:bg-gray-50 disabled:opacity-50"
                          style={{ border: '1px solid #E5E7EB', color: '#374151' }}>
                          Modifica
                        </button>
                        <button disabled={actionLoading}
                          onClick={() => onAction(step.step_id, 'reject', '', actionNotes[step.step_id] || 'Rifiutato')}
                          className="rounded-lg px-4 py-2 text-[13px] font-medium transition-colors hover:bg-red-50 disabled:opacity-50"
                          style={{ border: '1px solid #FCA5A5', color: '#DC2626' }}>
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <input type="text" placeholder="Nota (opzionale)"
                        value={actionNotes[step.step_id] || ''}
                        onChange={e => setActionNotes(prev => ({ ...prev, [step.step_id]: e.target.value }))}
                        className="w-full rounded-lg px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
                        style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}
                        data-testid={`step-note-${step.step_id}`} />
                    </div>
                  )}

                  {/* Pending message */}
                  {step.status === 'pending' && (
                    <p className="text-[12px] italic" style={{ color: '#9CA3AF' }}>
                      In attesa che lo step precedente venga completato
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/* ─── New Situation Dialog ─── */
function NewSituationDialog({ open, onClose, onCreated }) {
  const [description, setDescription] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [creating, setCreating] = useState(false);

  const analyze = async () => {
    if (!description.trim()) return;
    setAnalyzing(true);
    setAnalysis(null);
    setAnalysisId(null);
    try {
      const res = await api.post('/process/situations/analyze', { description });
      setAnalysis(res.data.analysis);
      setAnalysisId(res.data.analysis_id);
    } catch { toast.error("Errore nell'analisi della situazione"); }
    finally { setAnalyzing(false); }
  };

  const createBoard = async () => {
    if (!analysis) return;
    setCreating(true);
    try {
      const res = await api.post(`/process/situations/${analysisId || 'new'}/create-board`, {
        title: analysis.suggested_title || description.slice(0, 60),
        steps: analysis.suggested_steps || [],
        context: { topic: description },
      });
      toast.success('Board creata');
      onCreated(res.data);
      onClose();
      setDescription('');
      setAnalysis(null);
    } catch { toast.error('Errore nella creazione della board'); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setAnalysis(null); setAnalysisId(null); } }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-[16px] font-bold" style={{ color: '#1B2B4B' }}>Nuova Situazione</DialogTitle>
        </DialogHeader>
        {!analysis ? (
          <div className="space-y-3">
            <p className="text-[13px]" style={{ color: '#7C8CA2' }}>
              Descrivi cosa devi fare. L'Operator analizzerà l'esigenza e suggerirà le procedure più adatte.
            </p>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Es: Un potenziale partner mi ha contattato via LinkedIn per co-organizzare un evento sull'AI in Ticino..."
              className="w-full min-h-[120px] rounded-lg px-4 py-3 text-[14px] outline-none resize-none focus:ring-2 focus:ring-[#2563EB]/20"
              style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}
              data-testid="situation-input" />
            <button onClick={analyze} disabled={analyzing || !description.trim()}
              className="w-full py-2.5 rounded-lg text-[14px] font-medium text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#2563EB' }}
              data-testid="situation-analyze-btn">
              {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analisi in corso...</>
                : <><Eye className="w-4 h-4" /> Analizza con Operator</>}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {analysis.matched_tasks?.length > 0 && (
              <div>
                <h4 className="text-[12px] font-semibold uppercase mb-1.5" style={{ color: '#7C8CA2' }}>Procedure trovate</h4>
                <div className="space-y-1">
                  {analysis.matched_tasks.map((t, i) => (
                    <div key={i} className="flex items-start gap-2 rounded p-2 text-[13px]" style={{ backgroundColor: '#F3F4F6' }}>
                      <span className="rounded px-1.5 py-0.5 text-[10px] font-medium flex-shrink-0"
                        style={{
                          backgroundColor: t.relevance === 'alta' ? 'rgba(22,163,74,0.1)' : t.relevance === 'media' ? 'rgba(217,119,6,0.1)' : '#F3F4F6',
                          color: t.relevance === 'alta' ? '#16A34A' : t.relevance === 'media' ? '#D97706' : '#7C8CA2',
                        }}>{t.relevance}</span>
                      <div>
                        <span className="font-medium" style={{ color: '#1B2B4B' }}>[{t.task_id}] {t.task_name}</span>
                        <p className="text-[11px] mt-0.5" style={{ color: '#7C8CA2' }}>{t.match_reason}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {analysis.suggested_steps?.length > 0 && (
              <div>
                <h4 className="text-[12px] font-semibold uppercase mb-1.5" style={{ color: '#7C8CA2' }}>
                  Board suggerita ({analysis.suggested_steps.length} step)
                </h4>
                <div className="space-y-1">
                  {analysis.suggested_steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-[13px] py-1">
                      <span className="text-[11px] w-4" style={{ color: '#9CA3AF' }}>{i + 1}.</span>
                      <span style={{ color: '#1B2B4B' }}>{s.title}</span>
                      {s.requires_approval && (
                        <span className="text-[9px] rounded px-1 py-0.5 font-medium"
                          style={{ backgroundColor: 'rgba(124,58,237,0.1)', color: '#7C3AED' }}>checkpoint</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {analysis.notes && (
              <p className="text-[12px] rounded p-2" style={{ backgroundColor: '#EFF6FF', color: '#2563EB' }}>{analysis.notes}</p>
            )}
            <div className="flex gap-2 pt-2">
              <button onClick={createBoard} disabled={creating}
                className="flex-1 py-2.5 rounded-lg text-[14px] font-medium text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#2563EB' }}
                data-testid="situation-create-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Crea Board</>}
              </button>
              <button onClick={() => setAnalysis(null)}
                className="px-4 py-2.5 rounded-lg text-[13px] font-medium hover:bg-gray-50"
                style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}>
                <Edit3 className="w-3 h-3 inline mr-1" /> Modifica
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ─── Main Page ─── */
export default function BoardPage() {
  const [searchParams] = useSearchParams();
  const [boards, setBoards] = useState([]);
  const [activeBoard, setActiveBoard] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSituation, setShowSituation] = useState(false);
  const [filter, setFilter] = useState('all');

  const loadData = useCallback(async () => {
    try {
      const [bRes, tRes] = await Promise.allSettled([
        api.get('/process/boards'),
        api.get('/process/templates'),
      ]);
      if (bRes.status === 'fulfilled') setBoards(bRes.value.data || []);
      // Seed templates if needed
      if (tRes.status === 'rejected' || (tRes.status === 'fulfilled' && (tRes.value.data || []).length === 0)) {
        try { await api.post('/process/templates/seed'); } catch {}
      }
      const boardId = searchParams.get('board_id');
      if (boardId) await selectBoard(boardId);
    } catch {}
    finally { setLoading(false); }
  }, [searchParams]);

  useEffect(() => { loadData(); }, [loadData]);

  const selectBoard = async (boardId) => {
    try {
      const b = await api.get(`/process/boards/${boardId}`);
      setActiveBoard(b.data);
      const runs = await api.get(`/process/runs?board_id=${boardId}`);
      setActiveRun(runs.data?.length > 0 ? runs.data[0] : null);
    } catch { toast.error('Errore caricamento board'); }
  };

  const runBoard = async () => {
    if (!activeBoard) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/process/boards/${activeBoard.id}/run`);
      toast.success(res.data.status === 'paused'
        ? `Processo in attesa: ${res.data.paused_at_step_title}`
        : `Processo avviato: ${res.data.steps_completed} step completati`);
      await selectBoard(activeBoard.id);
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore avvio processo'); }
    finally { setActionLoading(false); }
  };

  const handleStepAction = async (stepId, action, content, note) => {
    if (!activeRun) return;
    setActionLoading(true);
    try {
      await api.post(`/process/runs/${activeRun.id}/steps/${stepId}/action`, { action, content, note });
      toast.success(
        action === 'approve' ? 'Approvato — il processo continua' :
        action === 'modify' ? 'Modificato e approvato' :
        action === 'reject' ? 'Rifiutato — processo fermato' : 'Azione completata'
      );
      await selectBoard(activeBoard.id);
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore azione'); }
    finally { setActionLoading(false); }
  };

  const handleDuplicate = async () => {
    if (!activeBoard) return;
    try {
      await api.post(`/process/boards/${activeBoard.id}/duplicate`);
      toast.success('Processo duplicato');
      await loadData();
    } catch { toast.error('Duplica processo non disponibile'); }
  };

  const handleSaveTemplate = async () => {
    if (!activeBoard) return;
    try {
      await api.post(`/process/boards/${activeBoard.id}/save-as-template`);
      toast.success('Salvato come template');
    } catch { toast.error('Salva come template non disponibile'); }
  };

  const handleDelete = async () => {
    if (!activeBoard) return;
    try {
      await api.delete(`/process/boards/${activeBoard.id}`);
      toast.success('Processo annullato');
      setActiveBoard(null);
      setActiveRun(null);
      await loadData();
    } catch { toast.error('Errore annullamento'); }
  };

  const handleBoardCreated = async (board) => {
    await loadData();
    await selectBoard(board.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: '#FAFBFC' }}
        data-testid="board-loading">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2563EB' }} />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="board-page" style={{ backgroundColor: '#FAFBFC' }}>
      <ProcessList
        boards={boards}
        selectedId={activeBoard?.id}
        onSelect={selectBoard}
        onNewSituation={() => setShowSituation(true)}
        filter={filter}
        setFilter={setFilter}
      />
      <ProcessDetail
        board={activeBoard}
        run={activeRun}
        onAction={handleStepAction}
        actionLoading={actionLoading}
        onDuplicate={handleDuplicate}
        onSaveTemplate={handleSaveTemplate}
        onDelete={handleDelete}
        onRun={runBoard}
      />
      <NewSituationDialog
        open={showSituation}
        onClose={() => setShowSituation(false)}
        onCreated={handleBoardCreated}
      />
    </div>
  );
}
